import { AxiosError } from 'axios';
import type { ErrorResponse } from '../types/api.types';
import { ERROR_KEY_TO_FIELD_MAP, STATUS_CODE_MESSAGES, ERROR_CODE_MESSAGES } from '../constants/api';

const ERROR_CODE_MESSAGES_LOWER: Partial<Record<string, string>> = Object.fromEntries(
  Object.entries(ERROR_CODE_MESSAGES).map(([key, value]) => [key.toLowerCase(), value])
);

const ERROR_KEY_TO_FIELD_MAP_LOWER: Partial<Record<string, string>> = Object.fromEntries(
  Object.entries(ERROR_KEY_TO_FIELD_MAP).map(([key, value]) => [key.toLowerCase(), value])
);

function lookupErrorMessage(errorKey: string): string | undefined {
  return ERROR_CODE_MESSAGES_LOWER[errorKey.toLowerCase()];
}

function extractFieldName(errorKey: string): string | null {
  const lowerKey = errorKey.toLowerCase();
  for (const [apiKey, fieldName] of Object.entries(ERROR_KEY_TO_FIELD_MAP_LOWER)) {
    if (fieldName && lowerKey.startsWith(apiKey)) {
      return fieldName;
    }
  }

  return null;
}

export function parseApiValidationErrors(error: unknown): Record<string, string> | null {
  if (!error || !(error instanceof AxiosError)) {
    return null;
  }

  const errorResponse = error.response?.data as ErrorResponse | undefined;

  if (!errorResponse?.errors) {
    return null;
  }

  const fieldErrors: Record<string, string> = {};

  Object.entries(errorResponse.errors).forEach(([errorKey, messages]) => {
    if (Array.isArray(messages) && messages.length > 0) {
      const fieldName = extractFieldName(errorKey);
      if (fieldName) {
        const translatedMessage = lookupErrorMessage(errorKey) || 'Błąd walidacji';
        fieldErrors[fieldName] = translatedMessage;
      }
    }
  });

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

export function parseApiValidationErrorsWithMessages(error: unknown): string[] | null {
  if (!error || !(error instanceof AxiosError)) {
    return null;
  }

  const errorResponse = error.response?.data as ErrorResponse | undefined;

  if (!errorResponse?.errors) {
    return null;
  }

  const errorMessages: string[] = [];

  Object.entries(errorResponse.errors).forEach(([errorKey, messages]) => {
    if (Array.isArray(messages) && messages.length > 0) {
      const translatedMessage = lookupErrorMessage(errorKey) || 'Błąd';
      errorMessages.push(translatedMessage);
    }
  });

  return errorMessages.length > 0 ? errorMessages : null;
}

export function hasApiErrorCode(error: unknown, code: string): boolean {
  if (!error || !(error instanceof AxiosError)) {
    return false;
  }

  const errorResponse = error.response?.data as ErrorResponse | undefined;
  if (!errorResponse?.errors) {
    return false;
  }

  const target = code.toLowerCase();
  return Object.keys(errorResponse.errors).some((key) => key.toLowerCase() === target);
}

export function getApiErrorMessage(error: unknown, fallback = 'Wystąpił błąd'): string {
  if (!error || !(error instanceof AxiosError)) {
    return fallback;
  }

  const errorResponse = error.response?.data as ErrorResponse | undefined;

  const errorKey = errorResponse?.errors ? Object.keys(errorResponse.errors)[0] : undefined;
  if (errorKey) {
    const translatedMessage = lookupErrorMessage(errorKey);
    if (translatedMessage) {
      return translatedMessage;
    }
  }

  const statusCode = error.response?.status;
  if (statusCode && STATUS_CODE_MESSAGES[statusCode]) {
    return STATUS_CODE_MESSAGES[statusCode];
  }

  return fallback;
}
